import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useSalveazaTargetareMapare } from "@/hooks/api/mutate/useSalveazaTargetareMapare";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type {
  TargetareCodMapat,
  TargetareColoanaMapare,
} from "@/types/targetare";
import {
  ETICHETE_COLOANA_MAPARE,
  codurileDinText,
  versiuneaColoanei,
} from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";

type EditorCoduriProps = {
  entityId: string;
  serviciu: string;
  coloana: TargetareColoanaMapare;
  coduri: TargetareCodMapat[];
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
};

export function descriereCod(c: TargetareCodMapat): string {
  return [
    c.descriere ?? "cod inexistent în nomenclator",
    `${formatNumar(c.firme_principal)} firme cu CAEN principal (${formatNumar(c.firme_principal_scop)} mari/mijlocii)`,
    `${formatNumar(c.firme_autorizat)} firme cu codul autorizat (${formatNumar(c.firme_autorizat_scop)} mari/mijlocii)`,
    c.alte_servicii.length > 0
      ? `mapat și la: ${c.alte_servicii.join(", ")}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
}

export function EditorCoduri({
  entityId,
  serviciu,
  coloana,
  coduri,
  onFirme,
}: EditorCoduriProps) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareMapare();
  const [editare, setEditare] = useState(false);
  const [text, setText] = useState("");
  const versiune = versiuneaColoanei(coloana);
  const secundar = coloana.endsWith("secundare");

  const incepe = () => {
    setText(coduri.map((c) => c.cod).join(", "));
    setEditare(true);
  };

  const confirma = () => {
    salveaza.mutate(
      { entity_id: entityId, coloana, coduri: codurileDinText(text) },
      {
        onSuccess: () => {
          setEditare(false);
          toast({
            title: "Mapare salvată",
            description: `${ETICHETE_COLOANA_MAPARE[coloana]} pentru ${serviciu}. Rulează reîncadrarea ca firmele să reflecte schimbarea.`,
          });
        },
        onError: (e) =>
          toast({
            title: "Maparea nu a fost salvată",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {secundar ? "Secundare" : "Principale"}
        </p>
        {!editare && (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title={`Editează ${ETICHETE_COLOANA_MAPARE[coloana]}`}
            onClick={incepe}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {editare ? (
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="font-mono text-xs"
            placeholder="coduri CAEN de 4 cifre, separate prin virgulă"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7"
              onClick={confirma}
              disabled={salveaza.isPending}
            >
              <Check className="mr-1 h-3 w-3" />
              Salvează
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => setEditare(false)}
              disabled={salveaza.isPending}
            >
              <X className="mr-1 h-3 w-3" />
              Renunță
            </Button>
            <span className="text-xs text-muted-foreground">
              Se validează după nomenclatorul CAEN Rev. {versiune}.
            </span>
          </div>
        </div>
      ) : coduri.length === 0 ? (
        <p className="text-xs text-muted-foreground">–</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {coduri.map((c) => (
            <button
              key={c.cod}
              type="button"
              title={descriereCod(c)}
              onClick={() =>
                onFirme({
                  criteriu: {
                    tip: "caen",
                    cod: c.cod,
                    versiune: c.versiune,
                    fel: "oricare",
                  },
                  titlu: `Firme cu CAEN ${c.cod} (Rev. ${c.versiune})${c.descriere ? ` „${c.descriere}”` : ""}, principal sau autorizat`,
                })
              }
              className={cn(
                "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:bg-muted",
                !c.valid &&
                  "border-destructive/60 bg-destructive/10 text-destructive",
                c.alte_servicii.length > 0 && c.valid && "border-amber-300"
              )}
            >
              <span className="font-mono font-semibold">{c.cod}</span>
              <span className="tabular-nums text-muted-foreground">
                {formatNumar(c.firme_principal_scop)}/
                {formatNumar(c.firme_principal)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
